import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function ForgotPasswordDialog({ open, onOpenChange }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSubmitted(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit request");
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    setEmail("");
    setSubmitted(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); else onOpenChange(v); }}>
      <DialogContent className="sm:max-w-md" data-testid="forgot-dialog">
        <DialogHeader>
          <DialogTitle className="font-medium tracking-tight">Forgot your password?</DialogTitle>
          <DialogDescription className="text-stone-500">
            {submitted
              ? "Your request has been sent for admin approval. The administrator will share a temporary password with you once approved."
              : "Enter the email you registered with. The administrator will be notified to issue you a new password."}
          </DialogDescription>
        </DialogHeader>

        {!submitted ? (
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="fp-email">Email</Label>
              <Input
                id="fp-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                data-testid="forgot-email-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={close} data-testid="forgot-cancel">Cancel</Button>
              <Button
                type="submit"
                disabled={loading}
                className="bg-emerald-900 hover:bg-emerald-800 text-stone-50"
                data-testid="forgot-submit"
              >
                {loading ? "Sending…" : "Request reset"}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <div className="pt-2">
            <Button onClick={close} className="w-full bg-emerald-900 hover:bg-emerald-800 text-stone-50" data-testid="forgot-done">
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
